/**
 * IQ streams thinking chunks that can arrive as JSON / Python-dict payloads carrying
 * escaped HTML (`{'type': 'text', 'text': '&lt;p&gt;Checking the video…&lt;/p&gt;'}`).
 * These helpers turn that raw stream into readable plain text before it is displayed.
 */

const NAMED_ENTITIES = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
  ndash: "–",
  mdash: "—",
  hellip: "…",
  lsquo: "‘",
  rsquo: "’",
  ldquo: "“",
  rdquo: "”",
  bull: "•",
};

const TEXT_FIELD_PATTERN = /["']text["']\s*:\s*(?:"((?:[^"\\]|\\.)*)"|'((?:[^'\\]|\\.)*)')/;
const HTML_TAG_PATTERN = /<\/?[a-z][a-z0-9]*(?:\s[^>]*)?\/?>/i;

/** Keys that mark a structured response body (quiz, inspection, validation), never thinking. */
const PAYLOAD_KEY_PATTERN =
  /["'](?:questions|steps|quiz|quiz_title|validation|table_of_contents|chapters|inspection_instructions|instructions|suggestions|step_type|what_to_look_for|video_title|answer)["']\s*:/;

/** How far past an opening brace to look for a payload key before treating it as prose. */
const PAYLOAD_LOOKAHEAD = 200;

const isStructuredChunk = (text) =>
  /["'](?:type|text)["']\s*:/.test(text) || text.trim().startsWith("[{");

/** `signature` / `extras` chunks carry model metadata, never user-facing thinking. */
const isMetadataChunk = (text) => /["'](?:signature|extras)["']/.test(text);

const containsHtml = (text) => HTML_TAG_PATTERN.test(text);

/** Fenced blocks carry the streamed response body, so drop them content and all. */
function stripCodeBlocks(text) {
  return text.replace(/```[\s\S]*?```/g, "").replace(/```[\s\S]*$/, "");
}

/**
 * IQ streams the structured response body on the same channel as its thinking.
 * Everything from where that body starts is dropped so the prose stays readable.
 */
function stripResponsePayload(text) {
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (char !== "{" && char !== "[") continue;
    if (PAYLOAD_KEY_PATTERN.test(text.slice(index, index + PAYLOAD_LOOKAHEAD))) {
      return text.slice(0, index);
    }
  }
  return text;
}

function decodeHtmlEntities(text) {
  return text.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z]+);/gi, (match, entity) => {
    const key = entity.toLowerCase();
    if (key.startsWith("#x")) {
      const code = Number.parseInt(key.slice(2), 16);
      return Number.isNaN(code) ? match : String.fromCodePoint(code);
    }
    if (key.startsWith("#")) {
      const code = Number.parseInt(key.slice(1), 10);
      return Number.isNaN(code) ? match : String.fromCodePoint(code);
    }
    return NAMED_ENTITIES[key] ?? match;
  });
}

function unescapeSequences(text) {
  return text
    .replace(/\\u([0-9a-f]{4})/gi, (match, hex) => String.fromCharCode(Number.parseInt(hex, 16)))
    .replace(/\\r\\n|\\r|\\n/g, "\n")
    .replace(/\\t/g, " ")
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
    .replace(/\\\\/g, "\\");
}

/** Pull the `text` field out of each streamed dict chunk, dropping metadata chunks. */
function extractStreamedText(raw) {
  if (!isStructuredChunk(raw)) return raw;

  const parts = raw.split(/\}\s*,?\s*\{/);
  const texts = [];

  parts.forEach((part, index) => {
    let chunk = part;
    if (index > 0) chunk = `{${chunk}`;
    if (index < parts.length - 1) chunk = `${chunk}}`;
    if (isMetadataChunk(chunk)) return;

    const match = TEXT_FIELD_PATTERN.exec(chunk);
    if (match) texts.push(match[1] ?? match[2] ?? "");
  });

  return texts.length ? texts.join("") : raw;
}

/** Keep block boundaries as newlines and bold/italic as markdown, drop every other tag. */
function htmlToText(html) {
  return html
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<li[^>]*>/gi, "\n- ")
    .replace(/<\/li>/gi, "")
    .replace(/<\/(?:p|div|section|article|h[1-6]|tr|blockquote|pre)>/gi, "\n\n")
    .replace(/<\/?(?:ul|ol|table|tbody|thead)[^>]*>/gi, "\n")
    .replace(/<\/?(?:strong|b)[^>]*>/gi, "**")
    .replace(/<\/?(?:em|i)[^>]*>/gi, "*")
    .replace(/<[^>]+>/g, "");
}

function normalizeWhitespace(text) {
  return text
    .replace(/\r\n?/g, "\n")
    .replace(/[ \t\u00a0]+/g, " ")
    .split("\n")
    .map((line) => line.trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Convert a raw streamed thinking payload into readable text.
 *
 * @param {string} raw Accumulated thinking stream (JSON/dict chunks, HTML, markdown).
 * @returns {string} Readable text with paragraph newlines and markdown emphasis kept.
 */
export function parseIQThinkingText(raw) {
  const source = String(raw ?? "");
  if (!source.trim()) return "";

  let text = unescapeSequences(extractStreamedText(source));
  text = stripResponsePayload(stripCodeBlocks(text));
  if (!text.trim()) return "";

  text = decodeHtmlEntities(text);
  if (containsHtml(text)) {
    text = decodeHtmlEntities(htmlToText(text));
  }

  return normalizeWhitespace(text);
}

/**
 * Readable thinking split into display lines (paragraphs and list items).
 *
 * @param {string} raw Accumulated thinking stream.
 * @returns {string[]} Non-empty readable lines in stream order.
 */
export function parseIQThinkingLines(raw) {
  const text = parseIQThinkingText(raw);
  if (!text) return [];

  return text
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
}
